import TreesUtil from '../TreesUtil';
import { guidAt, measure } from './TreeChunk';
import { ROW, OVERRIDES, NESTED, COLUMNS } from './TreeSymbols';

// Façade Proxy, pas d'accesseurs de prototype : invisibles à l'étalement et à JSON.stringify. Voir docs/binary-trees/GUIDE-BINAIRE.md

// Ces deux ensembles sont à tenir à jour à la main : un oubli ne provoque aucune erreur (COOKBOOK.md §3.2).
const DATE_COLUMNS = new Set([
    'properties.creationDate',
    'properties.modificationDate',
    'properties.plantingDate',
    'properties.deathDate',
    'deletionDate'
]);

const NULLABLE_INT_COLUMNS = new Set(['properties.age', 'properties.ageAtPlantation']);

const IS_VIEW = 'isColumnarView';

function buildReader(chunk, column) {
    const name = column.name;

    switch (column.kind) {
        case 'i32': {
            const values = chunk.int32(name);
            if (NULLABLE_INT_COLUMNS.has(name)) return row => (values[row] < 0 ? null : values[row]);
            return row => values[row];
        }

        case 'f32': {
            const values = chunk.float32(name);
            return row => values[row];
        }

        case 'f64': {
            const values = chunk.float64(name);
            // `NaN` signifie « pas de date ».
            if (DATE_COLUMNS.has(name)) return row => (Number.isNaN(values[row]) ? null : new Date(values[row]));
            return row => values[row];
        }

        case 'dict32': return chunk.dictReader(name);
        case 'utf8': return chunk.utf8Reader(name);
        case 'guid': return chunk.guidReader(name);

        case 'list_i32': {
            const { offsets, values } = chunk.listInt32(name);
            return row => {
                const start = offsets[row], end = offsets[row + 1];
                // Tableau vide gelé et partagé : ne jamais muter en place.
                if (end === start) return TreesUtil.EMPTY_LIST;
                return Array.from(values.subarray(start, end));
            };
        }

        case 'list_dict32': {
            const { offsets, values, entries } = chunk.listDict(name);
            return row => {
                const start = offsets[row], end = offsets[row + 1];
                if (end === start) return TreesUtil.EMPTY_LIST;
                const result = new Array(end - start);
                for (let k = start; k < end; k++) result[k - start] = entries[values[k]];
                return result;
            };
        }

        case 'list_json': {
            const { offsets, read } = chunk.listJson(name);
            return row => {
                const start = offsets[row], end = offsets[row + 1];
                if (end === start) return TreesUtil.EMPTY_LIST;
                const result = new Array(end - start);
                for (let k = start; k < end; k++) result[k - start] = read(k);
                return result;
            };
        }

        default:
            return null;
    }
}

function makeTarget(row) {
    const target = {};
    target[ROW] = row;
    target[OVERRIDES] = null;
    return target;
}

function writeOverride(target, key, value) {
    let overrides = target[OVERRIDES];
    if (overrides === null) target[OVERRIDES] = overrides = Object.create(null);
    overrides[key] = value;
}

function makeHandler(readers, readSpecial = null, specialKeys = [], { sparse = false } = {}) {
    const readerKeys = [...readers.keys()];
    const allKeys = specialKeys.length > 0 ? [...new Set([...specialKeys, ...readerKeys])] : readerKeys;
    const knows = key => readers.has(key) || specialKeys.includes(key);

    const readValue = (target, key) => {
        const overrides = target[OVERRIDES];
        if (overrides !== null && key in overrides) return overrides[key];

        if (readSpecial !== null) {
            const special = readSpecial(target, key);
            if (special !== undefined) return special;
        }

        const read = readers.get(key);
        if (read !== undefined) {
            const value = read(target[ROW]);
            // Clairsemé : une colonne sans valeur se comporte comme une clé absente.
            return sparse && value === null ? undefined : value;
        }

        if (key === IS_VIEW) return true;
        return target[key];
    };

    const exists = (target, key) => readValue(target, key) !== undefined;

    return {
        get(target, key) {
            return readValue(target, key);
        },

        set(target, key, value) {
            if (knows(key)) writeOverride(target, key, value);
            else target[key] = value;
            return true;
        },

        has(target, key) {
            if (sparse && readers.has(key)) return exists(target, key);
            return knows(key) || key === IS_VIEW || key in target;
        },

        deleteProperty(target, key) {
            if (knows(key)) writeOverride(target, key, undefined);
            const overrides = target[OVERRIDES];
            if (overrides !== null && !knows(key)) delete overrides[key];
            delete target[key];
            return true;
        },

        ownKeys(target) {
            const extra = Object.keys(target);
            const keys = sparse ? allKeys.filter(key => exists(target, key)) : allKeys;
            return extra.length === 0 ? keys : [...new Set([...keys, ...extra])];
        },

        // Requis pour que l'étalement et `Object.keys` voient les clés de `ownKeys`.
        getOwnPropertyDescriptor(target, key) {
            if (knows(key) || Object.prototype.hasOwnProperty.call(target, key)) {
                const value = readValue(target, key);
                if (sparse && value === undefined) return undefined;
                return { value, writable: true, enumerable: true, configurable: true };
            }
            return undefined;
        }
    };
}

export default function createTreeViewFactory(chunk) {
    const featureReaders = new Map();
    const propertyReaders = new Map();
    const dimensionReaders = new Map();
    const customFieldReaders = new Map();
    let trunks = null;

    for (const column of chunk.schema.columns) {
        const name = column.name;

        if (column.kind === 'flags') {
            const values = chunk.uint8(name);
            (column.bits || []).forEach((bit, position) => {
                const mask = 1 << position;
                propertyReaders.set(bit, row => (values[row] & mask) !== 0);
            });
            continue;
        }
        if (column.kind === 'list_struct') { trunks = chunk.listStruct(name); continue; }

        const reader = buildReader(chunk, column);
        if (!reader) continue;

        if (name.startsWith('properties.dimensions.')) dimensionReaders.set(name.slice('properties.dimensions.'.length), reader);
        else if (name.startsWith('properties.customFields.')) customFieldReaders.set(name.slice('properties.customFields.'.length), reader);
        else if (name.startsWith('properties.')) propertyReaders.set(name.slice('properties.'.length), reader);
        else featureReaders.set(name, reader);
    }

    // Transportés une seule fois, exposés au niveau du feature et dans `properties`.
    for (const alias of ['fetchId', 'projectId'])
        if (!propertyReaders.has(alias) && featureReaders.has(alias))
            propertyReaders.set(alias, featureReaders.get(alias));

    // Dérivés, non transportés, mais requis : l'API refuse un `Dimensions` sans clé primaire.
    if (dimensionReaders.size > 0) {
        const derived = { propertiesId: 'treeId', fetchId: 'fetchId', projectId: 'projectId' };
        for (const [name, source] of Object.entries(derived)) {
            const reader = propertyReaders.get(source);
            if (reader && !dimensionReaders.has(name)) dimensionReaders.set(name, reader);
        }
    }

    const dimensionsHandler = dimensionReaders.size > 0 ? makeHandler(dimensionReaders) : null;
    // Clairsemé : comme dans le JSON, `customFields` ne contient que les champs renseignés.
    const customFieldsHandler = customFieldReaders.size > 0 ? makeHandler(customFieldReaders, null, [], { sparse: true }) : null;

    // Mémorisé sur l'arbre : ne jamais le fabriquer en boucle sur tout le projet.
    const nested = (target, key, handler) => {
        let cache = target[NESTED];
        if (cache === undefined) target[NESTED] = cache = {};
        return cache[key] || (cache[key] = new Proxy(makeTarget(target[ROW]), handler));
    };

    const propertySpecialKeys = [];
    if (dimensionsHandler) propertySpecialKeys.push('dimensions');
    if (customFieldsHandler) propertySpecialKeys.push('customFields');
    if (trunks) propertySpecialKeys.push('trunks');

    let readProperty = null;

    // Reconstruits à chaque accès, volontairement : ne pas mémoriser.
    const readTrunks = (target) => {
        const row = target[ROW];
        const start = trunks.offsets[row], end = trunks.offsets[row + 1];
        if (end === start) return TreesUtil.EMPTY_LIST;

        const result = new Array(end - start);
        for (let k = start; k < end; k++)
            result[k - start] = {
                id: guidAt(trunks.id, k * 16),
                // `NaN` → `null`, pas 0 : sinon `getBiggestTrunk` retient un tronc non mesuré.
                height: measure(trunks.height[k]),
                circumference: measure(trunks.circumference[k]),
                crownDiameter: measure(trunks.crownDiameter[k]),
                order: k - start,
                propertiesId: readProperty(target, 'treeId'),
                projectId: readProperty(target, 'projectId'),
                fetchId: readProperty(target, 'fetchId')
            };
        return result;
    };

    // Qui lit ces colonnes DOIT vérifier `OVERRIDES` (et `NESTED`) avant : voir `TreesUtil.getTrunkColumns`.
    const columnarAccess = {
        trunks,
        observations: chunk.has('properties.observations') ? chunk.listJson('properties.observations') : null,
        dimensions: dimensionReaders.size > 0 ? dimensionReaders : null,
        customFields: customFieldReaders.size > 0 ? customFieldReaders : null
    };

    const readPropertySpecial = (target, key) => {
        if (key === 'dimensions' && dimensionsHandler) return nested(target, 'dimensions', dimensionsHandler);
        if (key === 'customFields' && customFieldsHandler) return nested(target, 'customFields', customFieldsHandler);
        if (key === 'trunks' && trunks) return readTrunks(target);
        if (key === COLUMNS) return columnarAccess;
        return undefined;
    };

    const propertiesHandler = makeHandler(propertyReaders, readPropertySpecial, propertySpecialKeys);
    readProperty = propertiesHandler.get;

    let readFeature = null;

    const readFeatureSpecial = (target, key) => {
        switch (key) {
            case 'type': return 'Feature';
            // Reconstruite à chaque accès : un cache retiendrait un objet par arbre.
            case 'geometry': return { type: 'Point', coordinates: [readFeature(target, 'lng'), readFeature(target, 'lat')] };
            case 'properties': return target.properties;
            default: return undefined;
        }
    };

    const featureHandler = makeHandler(featureReaders, readFeatureSpecial, ['type', 'geometry', 'properties']);
    readFeature = featureHandler.get;

    // `properties` doit rester remplaçable en entier (restauration d'historique) : pas de surcouche.
    const baseFeatureSet = featureHandler.set;
    featureHandler.set = (target, key, value) => {
        if (key === 'properties') { target.properties = value; return true; }
        return baseFeatureSet(target, key, value);
    };

    return function createTreeView(row) {
        const featureTarget = makeTarget(row);
        featureTarget.properties = new Proxy(makeTarget(row), propertiesHandler);
        return new Proxy(featureTarget, featureHandler);
    };
}
